import axios from "axios";
import axiosInstance from "./axiosInstance";
import Configs from "../utils/Configs";
import StorageKeys from "../constants/storageKeys";
import { cacheAccessTokenForServiceWorker } from "../utils/tokenIndexedDb";

const AuthApi = {
  /**
   * Updates the logged-in user's payment info.
   * Logic: Sends PayPal email and/or Mpesa number to the backend.
   */
  updatePaymentDetails: async (paypalEmail, mpesaNumber) => {
    try {
      const requestPayload = {
        paypal_email: paypalEmail,
        mpesa_number: mpesaNumber,
      };

      console.log("🚀 Updating Payment Details:", requestPayload);

      const response = await axiosInstance.post(
        Configs.updatePaymentDetailsEp,
        requestPayload,
      );

      console.log("✅ Payment Details Update Response:", response.data);
      return response.data; // { status: "success", data: "payment_details_updated_success" }
    } catch (error) {
      console.error("❌ Failed to update payment details:", error);
      if (error.response) {
        return error.response.data;
      }
      return { status: "fail", data: "network_error" };
    }
  },
  /**
   * Deletes a user account based on the provided user_id.
   * Authorization is handled automatically by axiosInstance.
   */
  deleteUser: async (userId) => {
    try {
      const requestPayload = {
        user_id: userId,
      };

      console.log("🚀 Delete User Request Payload:", requestPayload);

      // Using axiosInstance ensures the Bearer token is included in headers
      const response = await axiosInstance.post(
        Configs.deleteUserEp,
        requestPayload,
      );

      console.log("✅ Delete User API Response Data:", response.data);

      return response.data; // Returns {status: "success", data: "user_deletion_success"}
    } catch (error) {
      console.error("❌ Failed to delete user:", error);

      if (error.response) {
        console.error("Delete User Error Response:", error.response.data);
        console.error("Delete User Error Status:", error.response.status);

        // Return the server's error data (e.g., 'cannot_delete_parent_user')
        return error.response.data;
      } else if (error.request) {
        console.error(
          "Delete User Error Request (no response):",
          error.request,
        );
      } else {
        console.error("Delete User Error Message:", error.message);
      }

      // Return a fail object to keep the UI from crashing
      return {
        status: "fail",
        data: error.message || "network_error",
      };
    }
  },
  signIn: async (email, password) => {
    try {
      const response = await axios.post(
        Configs.loginEp,
        { email, password },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      // Check for a successful response before handling data
      if (response.data.status === "success") {
        const { access_token, refresh_token, ...userData } = response.data.data;

        // Store the tokens securely in local storage
        localStorage.setItem(StorageKeys.ACCESS_TOKEN, access_token);
        localStorage.setItem(StorageKeys.REFRESH_TOKEN, refresh_token);
        cacheAccessTokenForServiceWorker(access_token);

        // Return the user data and success status
        return { status: "success", data: userData };
      } else {
        // Return the failure message from the API
        return response.data;
      }
    } catch (error) {
      console.error("Failed to sign in:", error.message);
      // No response means the request never reached the server
      return {
        status: "fail",
        data: { message: error.response ? "server_error" : "network_error" },
      };
    }
  },

  resendCode: async (email) => {
    try {
      const response = await axios.post(Configs.sendCodeEp, { email });

      if (response.status === 200) {
        return response.data;
      } else {
        return {
          status: "error",
          data: `Failed to resend code. Status code: ${response.status}`,
        };
      }
    } catch (error) {
      return { status: "error", data: error.message };
    }
  },

  sendResetPasswordCode: async (email) => {
    try {
      const response = await axios.post(Configs.sendPasswordResetCodeEp, {
        email: email,
      });

      return response.data; // Returning the API response
    } catch (error) {
      throw new Error("Failed to send password reset code");
    }
  },

  changePassword: async ({ email, oldPassword, newPassword }) => {
    try {
      const requestPayload = {
        email: email,
        old_password: oldPassword,
        new_password: newPassword,
      };

      console.log("Change Password Request Payload:", requestPayload); // Log the request payload

      const response = await axios.post(
        Configs.changePasswordEp,
        requestPayload, // Send the payload
        {
          headers: {
            "Content-Type": "application/json; charset=UTF-8",
          },
        },
      );

      console.log("Change Password API Response Data:", response.data); // Log the response data

      return response.data; // Return the full response data
    } catch (error) {
      console.error("Failed to change password:", error);

      // Log the full error response if available (e.g., from Axios)
      if (error.response) {
        console.error(
          "Change Password Error Response (from server):",
          error.response.data,
        );
        console.error("Change Password Error Status:", error.response.status);
        console.error("Change Password Error Headers:", error.response.headers);
      } else if (error.request) {
        // The request was made but no response was received
        console.error(
          "Change Password Error Request (no response):",
          error.request,
        );
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error("Change Password Error Message:", error.message);
      }

      throw new Error("Failed to change password"); // Re-throw the error for component to handle
    }
  },

  resetPassword: async ({ email, resetCode, newPassword }) => {
    try {
      const response = await axios.post(
        Configs.resetPasswordEp,
        {
          email: email,
          reset_code: resetCode,
          new_password: newPassword,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return response.data; // Return the full response data
    } catch (error) {
      console.error("Failed to reset password:", error);
      throw new Error("Failed to reset password");
    }
  },

  signUp: async ({
    firstName,
    otherNames,
    pseudoName,
    upline,
    gender,
    email,
    phoneNumber,
    country,
    area,
    dateOfBirth,
    interestedIn,
    interestedInMinAge,
    interestedInMaxAge,
    password,
  }) => {
    try {
      const requestBody = {
        first_name: firstName,
        other_names: otherNames,
        pseudo_name: pseudoName,
        upline: upline,
        gender: gender.toUpperCase(),
        email: email,
        phone_number: phoneNumber,
        country: country,
        area: area,
        date_of_birth: dateOfBirth,
        interested_in: interestedIn,
        interested_in_min_age: interestedInMinAge,
        interested_in_max_age: interestedInMaxAge,
        password: password,
      };

      // Log the data before sending
      console.log("🚀 Final Request Body:", requestBody);

      const response = await axios.post(Configs.signupEp, requestBody, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      return response.data; // Returning API response
    } catch (error) {
      console.error("Failed to sign up:", error.message);
      throw new Error("Error signing up");
    }
  },
  createStaff: async ({
    firstName,
    otherNames,
    pseudoName,
    upline,
    email,
    phoneNumber,
    country,
    userrole,
  }) => {
    try {
      const requestBody = {
        first_name: firstName,
        other_names: otherNames,
        pseudo_name: pseudoName,
        upline: upline,
        email: email,
        phone_number: phoneNumber,
        country: country,
        userrole: userrole,
      };

      console.log("🚀 Create Staff Request Body:", requestBody);

      // Must be authenticated now that the backend enforces who may grant
      // which userrole — axiosInstance attaches the caller's Bearer token,
      // plain axios (as this used to call) sends none at all.
      const response = await axiosInstance.post(Configs.createStaffEp, requestBody);

      return response.data;
    } catch (error) {
      console.error("Failed to create staff:", error.message);
      throw new Error("Error creating staff");
    }
  },
};

export default AuthApi;
